import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2, FileArchive, Download, Send, Eye, ShieldCheck } from 'lucide-react';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
const n = (v) => (v || 0).toLocaleString();
const kb = (b) => `${Math.round((b || 0) / 1024)} KB`;

function downloadBlob(data, filename, type) {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function Builder({ api, runs, sets, onIssued }) {
  const [runId, setRunId] = useState('');
  const [picked, setPicked] = useState([]);
  const [client, setClient] = useState('');
  const [email, setEmail] = useState('');
  const [recipient, setRecipient] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState('');

  const extracts = (runs || []).filter(r => r.scope === 'extract');
  const run = extracts.find(r => r.run_id === runId);
  const clientName = client || run?.client || '';
  const approved = (sets || []).filter(s => s.status === 'approved'
    && (!clientName || (s.client || '').toLowerCase() === clientName.toLowerCase()));

  const body = () => ({
    run_id: runId, set_ids: picked, client: clientName,
    email: email.trim(), recipient_name: recipient.trim(), message: message.trim(),
  });

  const preview = async () => {
    setBusy('preview');
    try {
      const res = await api.post('/migration/packs/preview', body(), { responseType: 'blob', timeout: 180000 });
      downloadBlob(res.data, `${(clientName || 'client').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-toolkit-pack.zip`, 'application/zip');
      toast.success('Preview downloaded — nothing was filed or sent');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build the pack');
    }
    setBusy('');
  };

  const issue = async () => {
    setBusy('issue');
    try {
      const { data } = await api.post('/migration/packs', body(), { timeout: 180000 });
      if (data.email_note) toast.warning(data.email_note, { duration: 11000 });
      else if (data.email_status === 'sent') toast.success(`Pack sent to ${data.sent_to} and filed in the register`);
      else toast.success('Pack filed in the document register');
      onIssued();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not issue the pack');
    }
    setBusy('');
  };

  return (
    <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }}
      data-testid="pack-builder-card">
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate }}>Build a client pack</h3>
      <p className="text-[11px] mb-2" style={{ color: C.slate }}>
        One branded PDF — findings, readiness, the mapping decisions and who approved them, method and
        limitations, and what happens next — with the lineage register, the findings and the mapping document
        beside it as CSV, zipped. Only <b>approved</b> maps go in, and a map belonging to another client cannot.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-1.5 mb-2">
        <div>
          <p className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: C.slate }}>Profile run</p>
          <select value={runId} onChange={e => { setRunId(e.target.value); setPicked([]); }} data-testid="pack-run-select"
            className="w-full text-xs rounded-sm border px-2 h-8" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }}>
            <option value="">— choose the profile this pack reports on —</option>
            {extracts.map(r => (
              <option key={r.run_id} value={r.run_id}>
                {`${r.client || r.filename || 'extract'} · ${n(r.totals?.rows)} rows · ${(r.finished_at || '').slice(0, 10)}`}
              </option>
            ))}
          </select>
        </div>
        <Input value={client} onChange={e => setClient(e.target.value)} data-testid="pack-client-input"
          placeholder={run?.client ? `Prepared for (${run.client})` : 'Prepared for'} className="text-xs rounded-sm h-8 self-end" />
        <div className="self-end flex gap-1.5">
          <Button size="sm" variant="outline" onClick={preview} disabled={!runId || !!busy}
            className="text-xs rounded-sm h-8 flex-1" data-testid="pack-preview-btn">
            {busy === 'preview' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1" />}Preview
          </Button>
          <Button size="sm" onClick={issue} disabled={!runId || !!busy} className="text-xs rounded-sm h-8 flex-1"
            style={{ background: C.navy }} data-testid="pack-issue-btn">
            {busy === 'issue' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
            {email.trim() ? 'File & send' : 'File pack'}
          </Button>
        </div>
      </div>

      {!!runId && (
        <div className="mb-2" data-testid="pack-sets-picker">
          <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: C.slate }}>
            Agreed mapping decisions to include
          </p>
          {!approved.length ? (
            <p className="text-[11px]" style={{ color: C.amber }} data-testid="pack-no-approved-sets">
              No approved map for this client yet — the pack will say so plainly, which is better than
              implying a decision nobody made.
            </p>
          ) : approved.map(s => (
            <label key={s.set_id} className="flex items-center gap-1.5 text-[11px] cursor-pointer"
              style={{ color: C.navy }} data-testid={`pack-set-${s.set_id}`}>
              <input type="checkbox" checked={picked.includes(s.set_id)}
                onChange={e => setPicked(p => e.target.checked ? [...p, s.set_id] : p.filter(x => x !== s.set_id))} />
              <ShieldCheck className="w-3 h-3" style={{ color: C.green }} />
              {s.label} v{s.version} — approved by {s.approved?.by?.name || s.approved?.by?.email}
              {s.approved?.agreed_with ? `, agreed with ${s.approved.agreed_with}` : ''}
            </label>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-1.5">
        <Input value={email} onChange={e => setEmail(e.target.value)} data-testid="pack-email-input"
          placeholder="Send to (leave blank to just file it)" className="text-xs rounded-sm h-8" />
        <Input value={recipient} onChange={e => setRecipient(e.target.value)} data-testid="pack-recipient-input"
          placeholder="Recipient name" className="text-xs rounded-sm h-8" />
        <Input value={message} onChange={e => setMessage(e.target.value)} data-testid="pack-message-input"
          placeholder="A line of your own for the email" className="text-xs rounded-sm h-8" />
      </div>
      <p className="text-[10px] mt-1.5" style={{ color: C.slate }}>
        Outbound mail is deny-by-default: a prospect address has to be allow-listed before a pack can leave.
        If it is held you will be told exactly that — the pack is still filed and downloadable.
      </p>
    </Card>
  );
}

export function ClientPacks({ api, isAdmin, runs }) {
  const [sets, setSets] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => api.get('/migration/packs')
    .then(r => setPacks(r.data.packs || [])).catch(() => { }), [api]);

  useEffect(() => {
    Promise.all([api.get('/migration/mappings'), api.get('/migration/packs')])
      .then(([s, p]) => { setSets(s.data.sets || []); setPacks(p.data.packs || []); })
      .catch(() => toast.error('Failed to load client packs'))
      .finally(() => setLoading(false));
  }, [api]);

  const download = async (pack) => {
    try {
      const res = await api.get(`/dms/documents/${pack.document_id}/download`, { responseType: 'blob' });
      downloadBlob(res.data, `${(pack.business_name || 'client').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-toolkit-pack.zip`, 'application/zip');
      toast.success('Pack downloaded');
      load();
    } catch { toast.error('Could not download that pack'); }
  };

  if (loading) return <div className="text-sm p-6" style={{ color: C.slate }}>Loading client packs…</div>;

  return (
    <div className="space-y-3" data-testid="client-packs">
      {isAdmin ? <Builder api={api} runs={runs} sets={sets} onIssued={load} /> : (
        <p className="text-[11px] p-2 rounded-sm" style={{ background: 'rgba(136,150,168,0.08)', color: C.slate }}
          data-testid="packs-read-only-note">
          Building and sending a client pack is an admin action — everything already sent is listed here.
        </p>
      )}

      <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="packs-card">
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.slate }}>Packs issued</h3>
        {!packs.length ? (
          <p className="text-[11px]" style={{ color: C.slate }} data-testid="packs-empty">
            <FileArchive className="w-3 h-3 inline mr-1" />No pack has been issued yet.
          </p>
        ) : (
          <table className="w-full text-xs" data-testid="packs-table">
            <thead><tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
              <th className="py-1 pr-2">Prepared for</th><th className="py-1 pr-2">Issued</th>
              <th className="py-1 pr-2">Readiness</th><th className="py-1 pr-2">Findings</th>
              <th className="py-1 pr-2">Maps</th><th className="py-1 pr-2">Sent to</th>
              <th className="py-1 pr-2">Size</th><th className="py-1 pr-2">Reference</th><th className="py-1"></th>
            </tr></thead>
            <tbody>
              {packs.map(p => {
                const m = p.pack_manifest || {};
                return (
                  <tr key={p.document_id} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}
                    data-testid={`pack-row-${p.document_id}`}>
                    <td className="py-1.5 pr-2" style={{ color: C.navy }}>{p.business_name || '—'}</td>
                    <td className="py-1.5 pr-2 text-[10px]" style={{ color: C.slate }}>
                      {(p.created_at || '').slice(0, 16).replace('T', ' ')}<br />{p.created_by}
                    </td>
                    <td className="py-1.5 pr-2 font-mono">{m.readiness ?? '—'}</td>
                    <td className="py-1.5 pr-2 font-mono" style={{ color: m.material_findings ? C.red : C.slate }}>
                      {n(m.findings)}{m.material_findings ? ` · ${m.material_findings} material` : ''}
                    </td>
                    <td className="py-1.5 pr-2 text-[10px]" style={{ color: C.slate }}>
                      {(m.mapping_sets || []).length
                        ? (m.mapping_sets || []).map(s => `${s.label} v${s.version}`).join(', ')
                        : 'none'}
                    </td>
                    <td className="py-1.5 pr-2 text-[10px]"
                      style={{ color: p.email_status === 'sent' ? C.green : p.sent_to ? C.amber : C.slate }}>
                      {p.sent_to ? `${p.sent_to} (${p.email_status})` : 'not sent'}
                    </td>
                    <td className="py-1.5 pr-2 text-[10px]" style={{ color: C.slate }}>{kb(p.size_bytes)}</td>
                    <td className="py-1.5 pr-2 font-mono text-[10px]" style={{ color: C.slate }}
                      title={p.sha256}>{(p.sha256 || '').slice(0, 10)}</td>
                    <td className="py-1.5 text-right">
                      <Button size="sm" variant="ghost" onClick={() => download(p)} className="h-6 px-1.5 text-[10px]"
                        style={{ color: C.blue }} data-testid={`pack-download-${p.document_id}`}>
                        <Download className="w-3 h-3 mr-1" />Download
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p className="text-[10px] mt-2" style={{ color: C.slate }}>
          Every pack is filed in the document register with its own hash, so what was sent, to whom and when is
          answerable later — including each download.
        </p>
      </Card>
    </div>
  );
}
